import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTopBar } from '@/contexts/TopBarContext'
import { adminApi, type SupportRequestCategory } from '@/api/admin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LifeBuoy, Check } from 'lucide-react'
import { useAdminDates } from '@/hooks/useDates'

const CATEGORIES: { id: SupportRequestCategory; label: string }[] = [
  { id: 'Question', label: 'Question' },
  { id: 'Problem', label: 'Something’s not working' },
  { id: 'Billing', label: 'Billing' },
  { id: 'FeatureRequest', label: 'Feature request' },
  { id: 'Other', label: 'Other' },
]

function statusBadgeClass(status: string) {
  switch (status) {
    case 'Open': return 'bg-amber-50 text-amber-700 border-amber-200'
    case 'InProgress': return 'bg-blue-50 text-blue-700 border-blue-200'
    case 'Resolved': return 'bg-green-50 text-green-700 border-green-200'
    default: return 'bg-gray-100 text-gray-500 border-gray-200'
  }
}

function statusLabel(status: string) {
  return status === 'InProgress' ? 'In progress' : status
}

export default function SupportPage() {
  const { formatDate } = useAdminDates()
  const fmt = (date: string) => {
    return formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  useTopBar(<span className="text-sm font-medium text-gray-900">Help &amp; support</span>)
  const location = useLocation()
  const queryClient = useQueryClient()

  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [category, setCategory] = useState<SupportRequestCategory>('Question')
  const [phone, setPhone] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const { data: requests, isLoading } = useQuery({
    queryKey: ['my-support-requests'],
    queryFn: () => adminApi.getMySupportRequests().then(r => r.data),
  })

  const submitMutation = useMutation({
    mutationFn: () => adminApi.createSupportRequest({
      subject: subject.trim(),
      message: message.trim(),
      category,
      pageUrl: `${location.pathname}${location.search}`,
      phone: phone.trim() || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-support-requests'] })
      setSubject('')
      setMessage('')
      setCategory('Question')
      setPhone('')
      setSubmitted(true)
      setTimeout(() => setSubmitted(false), 4000)
    },
  })

  const canSubmit = subject.trim().length > 0 && message.trim().length > 0

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <LifeBuoy size={22} className="text-indigo-500" />
          Help &amp; support
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Raise a question or report a problem — we'll reply to your account email.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">New request</CardTitle></CardHeader>
        <CardContent>
          <form
            onSubmit={e => { e.preventDefault(); if (canSubmit) submitMutation.mutate() }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label>What's this about?</Label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full border cursor-pointer transition-colors ${
                      category === c.id
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="A short summary"
                maxLength={150}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="message">Message</Label>
              <textarea
                id="message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Include as much detail as you can — what you were trying to do, what happened, and any error messages."
                rows={6}
                maxLength={5000}
                required
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone number <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="If you'd like us to call you"
                maxLength={40}
                autoComplete="tel"
              />
            </div>

            <div className="flex items-center gap-3 pt-1">
              <Button type="submit" disabled={!canSubmit || submitMutation.isPending}>
                {submitMutation.isPending ? 'Sending…' : 'Send request'}
              </Button>
              {submitted && (
                <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                  <Check size={14} /> Sent — we'll get back to you by email
                </span>
              )}
              {submitMutation.isError && (
                <span className="text-sm text-red-600">Something went wrong. Please try again.</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Your requests</h2>
        {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
        {!isLoading && requests?.length === 0 && (
          <p className="text-sm text-gray-400">You haven't raised any requests yet.</p>
        )}
        <div className="space-y-2">
          {requests?.map(r => (
            <div key={r.id} className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${statusBadgeClass(r.status)}`}>
                {statusLabel(r.status)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{r.subject}</p>
                <p className="text-xs text-gray-400">{fmt(r.createdAt)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
